import axios, { isAxiosError } from "axios";
import { supportUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { buildAxiosAuthenticationHeaders } from "@src/cloud-connect/api/util/build-axios-authentication-headers";
import { errorSnackbar } from "@src/cloud-connect/snackbars/entities/snackbar-launcher";
import { TicketCommentAttachment } from "@src/types/support";
import store from "@src/reducers/store";

const triggerDownload = (href: string, filename: string) => {
  const link = document.createElement("a");
  link.href = href;
  link.download = filename;
  link.rel = "noopener noreferrer";

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

export const downloadLocalFile = async (
  file: File,
  filename = file.name || "download",
  downloadError?: string,
) => {
  try {
    const blobUrl = URL.createObjectURL(file);
    try {
      triggerDownload(blobUrl, filename.trim() || "download");
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch (error) {
    console.error("Local attachment download failed:", error);
    errorSnackbar(downloadError || "Unable to download attachment.");
  }
};

export const downloadSupportAttachment = async (
  attachment: TicketCommentAttachment,
  texts?: {
    downloadAttachmentError: string;
    downloadEmpty: string;
  },
) => {
  const namespace = store.getState().yourSlice.authContext?.customerNamespace;
  if (!namespace) {
    errorSnackbar(texts?.downloadAttachmentError);
    return;
  }

  const base = supportUrlEnvVar().replace(/\/+$/, "");
  const url = `${base}/api/v1/ns/${namespace}/attachments/${attachment.id}`;
  const resolvedFilename = (attachment.file_name || "").trim() || "download";

  try {
    const response = await axios.get<Blob>(url, {
      headers: {
        Authorization: buildAxiosAuthenticationHeaders().Authorization,
      },
      responseType: "blob",
    });

    const blob = response.data;
    if (!blob || blob.size === 0) {
      errorSnackbar("Downloaded file is empty.");
      return;
    }

    const blobUrl = URL.createObjectURL(blob);
    try {
      triggerDownload(blobUrl, resolvedFilename);
    } finally {
      URL.revokeObjectURL(blobUrl);
    }
  } catch (error) {
    if (isAxiosError(error)) {
      console.error("Attachment download HTTP error:", {
        status: error.response?.status,
        message: error.message,
      });
      errorSnackbar(` ${texts?.downloadAttachmentError} : ${error.message}`);
    } else {
      console.error("Attachment download failed:", error);
      errorSnackbar(texts?.downloadAttachmentError);
    }
  }
};
